import React, { useEffect, useRef, useState } from "react";

import {
  formatFractionInput,
  formatPercentageInput,
  isNullOrUndefined,
  isValidNonnegativeNumber,
  isValidPercentageDecimal,
  useGetFootprintingBeefManureAdditive,
  useGetFootprintingBeefPrimaryDataById,
  useGetManureAdditives,
  useKeyedFormList,
  useMutateFootprintingBeefManureAdditive,
  useMutateFootprintingBeefPrimaryData,
  useTranslation,
} from "utils";

import {
  ENUM_FARM_FOOTPRINTING_BEEF_MANURE_ADDITIVE_ACTIVE,
  ENUM_MANURE_ADDITIVE_UNITS,
  FARM_FOOTPRINTING_BEEF_MANURE_ADDITIVE_ACTIVE,
  UploadableManureAdditive,
} from "models";

import {
  Button,
  Card,
  Flex,
  Form,
  FormInstance,
  Icon,
  IconButton,
  InputNumber,
  InputNumberWithUnitSelector,
  KlimRadioGroup,
  Option,
  PageSpin,
  Select,
  Typography,
} from "components";

type BeefManureAdditiveFormProps = {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
};

const manureAdditivePlaceholder: UploadableManureAdditive = {
  id: undefined,
  manureAdditiveId: "",
  amountUsedM3: null,
  amountUsedM3Unit: null,
  enrichedSlurryPct: null,
};

const BeefManureAdditiveForm = ({ primaryDataId, readOnly, farmId, year }: BeefManureAdditiveFormProps) => {
  const { t } = useTranslation("footprintingBeef");
  const formRef = useRef<FormInstance>(null);

  const [manureAdditiveActive, setManureAdditiveActive] = useState<ENUM_FARM_FOOTPRINTING_BEEF_MANURE_ADDITIVE_ACTIVE | null>(null);

  const { data: beefPrimaryData, isLoading } = useGetFootprintingBeefPrimaryDataById(primaryDataId ?? "");
  const keyedManureAdditives = useKeyedFormList<UploadableManureAdditive>(formRef, { isReady: !isLoading });
  const { data: manureAdditivesQuery } = useGetFootprintingBeefManureAdditive(primaryDataId ?? "");
  const { data: beefManureAdditivesQuery } = useGetManureAdditives();

  const selectGroupName = "footprinting-beef-manure-additive-select";

  const { mutate: mutateBeefManureAdditives } = useMutateFootprintingBeefManureAdditive(
    farmId,
    year,
    {
      primaryDataId: primaryDataId ?? "",
      selectedManureAdditive: keyedManureAdditives.payload.filter((m) => !!m.manureAdditiveId),
    },
    primaryDataId
  );
  const { mutate: mutateBeefPrimaryData } = useMutateFootprintingBeefPrimaryData(farmId, year, { farmId, year, manureAdditiveActive }, primaryDataId);

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateBeefPrimaryData({});
        mutateBeefManureAdditives();
      }
    },
    [isLoading, mutateBeefPrimaryData, mutateBeefManureAdditives, readOnly]
  );

  useEffect(() => {
    if (manureAdditivesQuery) {
      keyedManureAdditives.actions.hydrate(
        manureAdditivesQuery.length
          ? manureAdditivesQuery.map((m) => ({
              id: m.id,
              manureAdditiveId: m.manureAdditiveId ?? "",
              amountUsedM3: m.amountUsedM3 ?? null,
              amountUsedM3Unit: m.amountUsedM3Unit ?? null,
              enrichedSlurryPct: m.enrichedSlurryPct ?? null,
            }))
          : [manureAdditivePlaceholder]
      );
    }
  }, [manureAdditivesQuery, keyedManureAdditives.actions]);

  useEffect(() => {
    if (beefPrimaryData) {
      setManureAdditiveActive((beefPrimaryData.manureAdditiveActive as ENUM_FARM_FOOTPRINTING_BEEF_MANURE_ADDITIVE_ACTIVE) ?? null);

      formRef.current?.setFieldsValue({
        manureAdditiveActive: beefPrimaryData.manureAdditiveActive ?? null,
      });

      if (beefPrimaryData.manureAdditiveActive != null) {
        keyedManureAdditives.actions.requestRehydrationValidation();
      }
    }
  }, [beefPrimaryData, keyedManureAdditives.actions]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Card border dataTestId="footprinting-beef-manure-additives-form" className="padding-100">
      <Form ref={formRef} preserve={false}>
        <Flex vertical gap={16}>
          <Flex align="center" gap={16} justify="space-between" className="padding-100">
            <Typography variant="paragraph-m" style={{ maxWidth: "64%" }}>
              {t("beefManureAdditiveForm.manureAdditivesQuestion")}
              <span className="required-asterisk">*</span>
            </Typography>
            <KlimRadioGroup
              required
              name="manureAdditiveActive"
              dataTestId="footprinting-beef-manure-additives-radio"
              disabled={readOnly}
              vertical={false}
              type="tertiary"
              size="large"
              onChange={({ target }) => {
                setManureAdditiveActive(target.value);
                if (target.value === FARM_FOOTPRINTING_BEEF_MANURE_ADDITIVE_ACTIVE.YES && keyedManureAdditives.entries.length === 0) {
                  keyedManureAdditives.actions.add(manureAdditivePlaceholder);
                }
              }}
              value={manureAdditiveActive}
              defaultValue={manureAdditiveActive}
              optionType="button"
              options={[FARM_FOOTPRINTING_BEEF_MANURE_ADDITIVE_ACTIVE.YES, FARM_FOOTPRINTING_BEEF_MANURE_ADDITIVE_ACTIVE.NO].map((option) => ({
                label: t(`beefManureAdditiveForm.manureAdditiveOptions.${option}`),
                value: option,
              }))}
            />
          </Flex>
          {manureAdditiveActive === FARM_FOOTPRINTING_BEEF_MANURE_ADDITIVE_ACTIVE.YES && (
            <Flex vertical gap={16}>
              {keyedManureAdditives.entries.map((additive, index) => {
                const selectGroupWithIndex = `${selectGroupName}-${index}`;

                return (
                  <Form.Item key={additive.clientKey} className="padding-left-100 padding-right-100">
                    <Flex vertical gap={16}>
                      <Flex justify="space-between" align="center" vertical={false}>
                        <Typography variant="h5" style={{ flex: 1 }}>
                          {t("beefManureAdditiveForm.additiveNumber", { count: index + 1 })}
                        </Typography>
                        <IconButton
                          type="tertiary"
                          disabled={readOnly}
                          dataTestId={`delete-beef-manure-additive-${index}`}
                          size="small"
                          iconName="tabler/trash"
                          onClick={() => {
                            keyedManureAdditives.actions.remove(additive.clientKey);
                            if (keyedManureAdditives.entries.length === 1) setManureAdditiveActive(null);
                          }}
                        />
                      </Flex>
                      <Select
                        disabled={readOnly}
                        name={`${selectGroupName}-${additive.clientKey}`}
                        dataTestId={selectGroupWithIndex}
                        label={t("beefManureAdditiveForm.question.manureAdditiveTypeQuestionInput")}
                        rules={[{ required: true, message: "" }]}
                        initialValue={beefManureAdditivesQuery?.find((m) => m.id === additive.manureAdditiveId)?.name || ""}
                        value={beefManureAdditivesQuery?.find((m) => m.id === additive.manureAdditiveId)?.name || ""}
                        onChange={(v) => {
                          const matchingPractice = beefManureAdditivesQuery?.find((m) => m.name === v);
                          keyedManureAdditives.actions.update(additive.clientKey, { manureAdditiveId: matchingPractice ? matchingPractice.id : "" });
                        }}
                        dropdownStyle={{ zIndex: 10010 }}
                      >
                        {(beefManureAdditivesQuery ?? []).map((beefManureAdditive) => {
                          const isAlreadyUsed = keyedManureAdditives.entries.some(
                            (a) => a.manureAdditiveId === beefManureAdditive.id && a.manureAdditiveId !== additive.manureAdditiveId
                          );
                          return (
                            <Option
                              disabled={readOnly || isAlreadyUsed}
                              key={`${selectGroupWithIndex}-${beefManureAdditive.id}`}
                              data-testid={`${selectGroupWithIndex}-${beefManureAdditive.id}`}
                              value={beefManureAdditive.name}
                            >
                              {beefManureAdditive.name}
                            </Option>
                          );
                        })}
                      </Select>

                      <InputNumberWithUnitSelector
                        key={`amount-${additive.clientKey}-${additive.manureAdditiveId}`}
                        className="floating-label"
                        disabled={readOnly}
                        dataTestId={`footprinting-beef-manure-additive-amount-input-${index}`}
                        name={`amountUsedM3-${additive.clientKey}`}
                        label={t("beefManureAdditiveForm.question.amountUsedQuestionInput")}
                        unitLabel={t("unit", { ns: "footprinting" })}
                        units={["kilograms", "liters"]}
                        unitDisplayOverride={{
                          kilograms: "kg / m³",
                          liters: "l / m³",
                        }}
                        value={additive.amountUsedM3}
                        initialValue={additive.amountUsedM3}
                        unitValue={additive.amountUsedM3Unit}
                        onChange={(v, unit) =>
                          keyedManureAdditives.actions.update(additive.clientKey, { amountUsedM3: v, amountUsedM3Unit: unit as ENUM_MANURE_ADDITIVE_UNITS })
                        }
                        validateFirst
                        rules={[
                          {
                            required: true,
                            validator: (_: unknown, v: number) => {
                              if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                                return Promise.reject(new Error(t("beefManureAdditiveForm.error.amountUsedError")));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      />

                      <InputNumber
                        disabled={readOnly}
                        name={`enrichedSlurryPct-${additive.clientKey}`}
                        dataTestId="footprinting-beef-manure-enriched-slurry-input"
                        label={t("beefManureAdditiveForm.question.enrichedSlurryQuestionInput")}
                        suffix="%"
                        value={formatFractionInput(additive.enrichedSlurryPct)}
                        initialValue={formatFractionInput(additive.enrichedSlurryPct)}
                        onChange={(v) => keyedManureAdditives.actions.update(additive.clientKey, { enrichedSlurryPct: formatPercentageInput(v) })}
                        validateFirst
                        rules={[
                          { required: true, message: t("beefManureAdditiveForm.error.validPercentage") },
                          { type: "number" },
                          {
                            validator: (_: unknown, v: number) => {
                              if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                                return Promise.reject(new Error(t("beefManureAdditiveForm.error.validPercentage")));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      />
                    </Flex>
                  </Form.Item>
                );
              })}
            </Flex>
          )}
          {manureAdditiveActive === FARM_FOOTPRINTING_BEEF_MANURE_ADDITIVE_ACTIVE.YES && (
            <Button
              type="text"
              dataTestId="add-beef-manure-additive-button"
              disabled={readOnly}
              onClick={() => keyedManureAdditives.actions.add(manureAdditivePlaceholder)}
            >
              <Icon iconName="klim/plus" className="icon inline" /> {t("beefManureAdditiveForm.add")}
            </Button>
          )}
        </Flex>
      </Form>
    </Card>
  );
};

BeefManureAdditiveForm.Key = "beef.manureAdditive";
export { BeefManureAdditiveForm };
